import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Minus, Plus, Trash2, ShoppingBag, Lock } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function CartDrawer() {
  const { items, setQty, remove, clear, subtotal, open, setOpen } = useCart();
  const { user } = useAuth();
  const [step, setStep] = useState("cart");
  const [placing, setPlacing] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", address: "", city: "", state: "", zip: "" });

  const shippingCost = subtotal >= 75 || subtotal === 0 ? 0 : 7.5;
  const total = subtotal + shippingCost;
  const progress = Math.min(subtotal / 75, 1) * 100;

  const close = (v) => {
    setOpen(v);
    if (!v) setTimeout(() => setStep("cart"), 300);
  };

  const placeOrder = async (e) => {
    e.preventDefault();
    setPlacing(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        kind: "order",
        origin_url: window.location.origin,
        email: (form.email || user?.email || "").toLowerCase(),
        items: items.map((i) => ({ product_id: i.id, qty: i.qty })),
        shipping: { name: form.name, address: form.address, city: form.city, state: form.state, zip: form.zip },
      });
      clear();
      toast.success("Redirecting to secure checkout...");
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setPlacing(false);
    }
  };

  const field = (key, label, props = {}) => (
    <div>
      <Label className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone-400">{label}</Label>
      <Input
        data-testid={`checkout-${key}-input`}
        required
        value={form[key]}
        onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
        className="mt-1.5 bg-obsidian border-gold/25 text-parchment focus-visible:ring-gold rounded-none"
        {...props}
      />
    </div>
  );

  return (
    <Sheet open={open} onOpenChange={close}>
      <SheetContent side="right" className="w-full sm:max-w-md bg-slate2 border-l border-gold/25 text-parchment p-0 flex flex-col" data-testid="cart-drawer">
        <SheetHeader className="px-6 py-5 border-b border-gold/15">
          <SheetTitle className="font-display uppercase tracking-[0.12em] text-parchment flex items-center gap-3">
            <ShoppingBag className="w-5 h-5 text-gold" />
            {step === "cart" ? "Your Kit" : "Checkout"}
          </SheetTitle>
        </SheetHeader>

        {step === "cart" && (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5" data-testid="cart-items-list">
              {items.length === 0 && (
                <p className="text-stone-400 text-sm font-serif italic text-base pt-8" data-testid="cart-empty-message">
                  Your kit is empty. The armory awaits.
                </p>
              )}
              {items.map((i) => (
                <div key={i.id} className="flex gap-4 border border-gold/10 bg-card2/60 p-3" data-testid={`cart-item-${i.id}`}>
                  <img src={i.image} alt={i.name} className="w-16 h-16 object-cover grayscale-[0.2]" />
                  <div className="flex-1 min-w-0">
                    <div className="font-condensed uppercase tracking-wider text-sm truncate">{i.name}</div>
                    <div className="font-mono text-gold text-sm mt-0.5">${i.price.toFixed(2)}</div>
                    <div className="flex items-center gap-2 mt-2">
                      <button data-testid={`cart-qty-minus-${i.id}`} onClick={() => setQty(i.id, i.qty - 1)} className="p-1 border border-gold/25 hover:border-gold text-stone-300 hover:text-gold transition-colors"><Minus className="w-3 h-3" /></button>
                      <span className="font-mono text-sm w-6 text-center" data-testid={`cart-qty-${i.id}`}>{i.qty}</span>
                      <button data-testid={`cart-qty-plus-${i.id}`} onClick={() => setQty(i.id, i.qty + 1)} className="p-1 border border-gold/25 hover:border-gold text-stone-300 hover:text-gold transition-colors"><Plus className="w-3 h-3" /></button>
                      <button data-testid={`cart-remove-${i.id}`} onClick={() => remove(i.id)} className="ml-auto p-1 text-stone-500 hover:text-crimson transition-colors"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {items.length > 0 && (
              <div className="border-t border-gold/15 px-6 py-5 space-y-4">
                <div>
                  <div className="flex justify-between font-mono text-xs text-stone-400 uppercase tracking-widest mb-2">
                    <span>{shippingCost === 0 ? "Free shipping unlocked" : `$${(75 - subtotal).toFixed(2)} to free shipping`}</span>
                    <span>${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="h-1 bg-obsidian overflow-hidden">
                    <div className="h-full bg-gold transition-[width] duration-500" style={{ width: `${progress}%` }} />
                  </div>
                </div>
                <div className="flex justify-between font-condensed uppercase tracking-widest text-lg">
                  <span>Subtotal</span>
                  <span className="text-gold" data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
                </div>
                <button
                  data-testid="checkout-start-button"
                  onClick={() => { setForm((f) => ({ ...f, email: f.email || user?.email || "" })); setStep("checkout"); }}
                  className="w-full gold-sheen bg-gold text-obsidian font-condensed uppercase tracking-[0.2em] font-semibold py-3.5 hover:bg-gold-light hover:shadow-[0_0_24px_rgba(212,175,55,0.35)] transition-[background-color,box-shadow] duration-300"
                >
                  Proceed to Checkout
                </button>
              </div>
            )}
          </>
        )}

        {step === "checkout" && (
          <form onSubmit={placeOrder} className="flex-1 overflow-y-auto px-6 py-5 space-y-4" data-testid="checkout-form">
            {field("name", "Full Name")}
            {field("email", "Email", { type: "email" })}
            {field("address", "Street Address")}
            <div className="grid grid-cols-2 gap-4">
              {field("city", "City")}
              {field("state", "State")}
            </div>
            {field("zip", "ZIP Code")}
            <div className="border border-gold/15 bg-obsidian/60 p-4 space-y-2 font-mono text-sm">
              {items.map((i) => (
                <div key={i.id} className="flex justify-between text-stone-300"><span>{i.qty} &times; {i.name}</span><span>${(i.qty * i.price).toFixed(2)}</span></div>
              ))}
              <div className="flex justify-between text-stone-400 border-t border-gold/10 pt-2"><span>Shipping</span><span>{shippingCost === 0 ? "FREE" : `$${shippingCost.toFixed(2)}`}</span></div>
              <div className="flex justify-between text-gold font-semibold"><span>Total</span><span data-testid="checkout-total">${total.toFixed(2)}</span></div>
            </div>
            <p className="font-mono text-[11px] text-stone-500 uppercase tracking-widest flex items-center gap-2">
              <Lock className="w-4 h-4 text-gold" /> Secure Stripe checkout — test card 4242 4242 4242 4242
            </p>
            <div className="flex gap-3 pt-2">
              <button type="button" data-testid="checkout-back-button" onClick={() => setStep("cart")} className="px-5 py-3 border border-gold/30 font-condensed uppercase tracking-widest text-sm hover:border-gold hover:text-gold transition-colors">Back</button>
              <button type="submit" data-testid="place-order-button" disabled={placing} className="flex-1 gold-sheen bg-gold text-obsidian font-condensed uppercase tracking-[0.2em] font-semibold py-3 hover:bg-gold-light transition-colors disabled:opacity-60">
                {placing ? "Redirecting..." : "Pay with Stripe"}
              </button>
            </div>
          </form>
        )}
      </SheetContent>
    </Sheet>
  );
}
